import React, { useState, useEffect } from 'react';
import { Card, Form, Input, Button, Select, Alert, message, Space, Typography } from 'antd';
import { SearchOutlined, SwapOutlined } from '@ant-design/icons';
import { useParams } from 'react-router-dom';
import { getLeadByPhoneNumber, getLeadById, assignLead, getSiteVisitForms, getSiteVisitFormsById } from '../services/apiService';
import authService from '../services/authService';

const { Title, Text } = Typography;

export default function LeadReassign() {
  const { companyId: paramCompanyId } = useParams();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [searching, setSearching] = useState(false);
  const [leadData, setLeadData] = useState(null);
  const [companyId, setCompanyId] = useState(paramCompanyId);
  const [managersWithEmails, setManagersWithEmails] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);

  useEffect(() => {
    const fetchUserAndCompany = async () => {
      try {
        const user = await authService.getUserDetails();
        setCurrentUser(user);
        
        const effectiveCompanyId = companyId || user?.company_id;
        
        if (!companyId && user?.company_id) {
          setCompanyId(user.company_id);
        }

        // Load managers from form meta
        if (effectiveCompanyId) {
          try {
            // Get forms - use user-specific endpoint if not superadmin
            const isSuperAdmin = user?.role_id === 'superadmin' || user?.role_id === 'Super Administrator';
            const userId = !isSuperAdmin ? (user?.user_id || user?.id) : null;
            
            const formsResponse = await getSiteVisitForms(effectiveCompanyId, userId);
            const forms = Array.isArray(formsResponse) 
              ? formsResponse 
              : Array.isArray(formsResponse?.data) 
                ? formsResponse.data 
                : [];
            
            ////console.log('Forms response:', forms);
            
            // Find the first active form
            const activeForm = forms.find(form => form?.meta?.active === true);
            
            if (activeForm) {
              const formId = activeForm.meta?.form_id || activeForm.id;
              //console.log('Active form found:', formId);
              
              // Get form details to extract managers from meta
              const formDetails = await getSiteVisitFormsById(effectiveCompanyId, formId);
              //console.log('Form details:', formDetails);
              
              // Extract managers from fields where type is "object"
              if (formDetails && formDetails?.fields) {
                const managerFields = [];
                const fields = formDetails.fields;
                
                //console.log('Form fields:', fields);
                
                // Check fields for object type with manager label
                if (Array.isArray(fields)) {
                  fields.forEach(field => {
                    // Check if field type is "object" and label contains "Manager"
                    if (field.type === 'object' && field.label && field.label.toLowerCase().includes('manager')) {
                      //console.log('Found manager field:', field.label, field);
                      
                      if (field.options && Array.isArray(field.options)) {
                        field.options.forEach(option => {
                          // Check for email_id (from API) or email
                          const email = option.email_id || option.email;
                          const name = option.name;
                          
                          if (email && name) {
                            managerFields.push({
                              name: name,
                              email: email,
                              label: field.label,
                              fieldKey: field.name,
                              user_id: option.user_id,
                              phone_number: option.phone_number
                            });
                          }
                        });
                      }
                    }
                  });
                }
                
                //console.log('Extracted managers from form fields:', managerFields);
                
                if (managerFields.length > 0) {
                  // Remove duplicates based on email
                  const uniqueManagers = managerFields.filter((manager, index, self) =>
                    index === self.findIndex((m) => m.email === manager.email)
                  );
                  setManagersWithEmails(uniqueManagers);
                  //console.log('Unique managers set:', uniqueManagers);
                } else {
                  console.warn('No managers found in form fields');
                }
              }
            } else {
              console.warn('No active form found');
            }
          } catch (err) {
            console.error('Failed to load managers from form:', err);
          }
        }
      } catch (err) {
        console.error('Failed to fetch user details:', err);
      }
    };

    fetchUserAndCompany();
  }, [companyId]);

  const handleSearch = async (values) => {
    const { phoneNumber } = values;
    
    if (!phoneNumber || phoneNumber.length !== 10) {
      message.error('Please enter a valid 10-digit phone number');
      return;
    }

    setSearching(true);
    setLeadData(null);
    // Don't clear managers - keep the ones loaded from form meta

    try {
      const searchResponse = await getLeadByPhoneNumber(companyId, phoneNumber);
      
      //console.log('Search response:', searchResponse);
      
      if (searchResponse.data && searchResponse.data.length > 0) {
        const searchResult = searchResponse.data[0];
        const leadId = searchResult.lead_id;
        
        // Get current assigned manager from user_assigned object in search response
        const userAssignedObject = searchResult.user_assigned;
        const currentAssignedManager = userAssignedObject?.name || null;
        const currentAssignedUserId = searchResult.user_assigned_to;
        
        //console.log('User assigned object:', userAssignedObject);
        //console.log('Current assigned manager name:', currentAssignedManager);
        //console.log('Current assigned user ID:', currentAssignedUserId);
        
        if (leadId) {
          const leadDetails = await getLeadById(companyId, leadId);
          //console.log('Lead details response:', leadDetails);
          
          if (leadDetails) {
            // Create userAssignedObject from assigned_to
            let userAssignedObject = null;
            if (leadDetails.assigned_to) {
              userAssignedObject = {
                user_id: leadDetails.assigned_to.id,
                id: leadDetails.assigned_to.id,
                name: leadDetails.assigned_to.name,
                email_id: leadDetails.assigned_to.user_phone || '',
                phone_number: leadDetails.assigned_to.user_phone || '',
                tracking_number: leadDetails.assigned_to.tracking_number || ''
              };
              //console.log('Created userAssignedObject from assigned_to:', userAssignedObject);
            }
            
            setLeadData(leadDetails);
            
            // Pre-fill current Sales Manager from userAssignedObject
            if (userAssignedObject && userAssignedObject.name) {
              form.setFieldValue('currentManager', userAssignedObject.name);
              form.setFieldValue('currentManagerEmail', userAssignedObject.email_id || userAssignedObject.phone_number || 'N/A');
              //console.log('Set current manager to:', userAssignedObject.name);
            } else if (currentAssignedManager) {
              // Fallback to search result user_assigned
              form.setFieldValue('currentManager', currentAssignedManager);
              form.setFieldValue('currentManagerEmail', userAssignedObject?.email_id || 'N/A');
              //console.log('Set current manager (from search) to:', currentAssignedManager);
            } else {
              // Fallback: Check lead_fields if assigned_to is not available
              const sourcingManager = leadDetails.lead_fields?.sourcing_manager_5fd;
              const closingManager = leadDetails.lead_fields?.closing_manager_cb3;
              const preSalesMember = leadDetails.lead_fields?.platform__copy__797;
              
              const fallbackManager = sourcingManager || closingManager || preSalesMember;
              
              if (fallbackManager) {
                const managerValue = Array.isArray(fallbackManager) ? fallbackManager[0] : fallbackManager;
                form.setFieldValue('currentManager', managerValue);
                //console.log('Set current manager (fallback) to:', managerValue);
              }
            }
            
            message.success('Lead found successfully');
          }
        }
      } else {
        message.warning('No lead found with this phone number');
      }
    } catch (error) {
      console.error('Error searching lead:', error);
      message.error(error.message || 'Failed to search lead');
    } finally {
      setSearching(false);
    }
  };

  const handleReassign = async (values) => {
    const { newManagerEmail } = values;
    
    if (!leadData) {
      message.error('Please search for a lead first');
      return;
    }

    if (!newManagerEmail) {
      message.error('Please select a new Sales Manager');
      return;
    }

    setLoading(true);

    try {
      const leadId = leadData.lead_id;
      
      //console.log('Assigning lead:', leadId, 'to manager email:', newManagerEmail);

      // Use the new assign API
      await assignLead(companyId, leadId, newManagerEmail);
      
      message.success('Lead reassigned successfully to ' + newManagerEmail);
      
      // Reset form and lead data
      form.resetFields();
      setLeadData(null);
    } catch (error) {
      console.error('Error reassigning lead:', error);
      message.error(error.message || 'Failed to reassign lead');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ maxWidth: 800, margin: '0 auto' }}>
      <Card>
        <Title level={3}>
          <SwapOutlined /> Lead Re-assign
        </Title>
        <Text type="secondary">
          Search for a lead by phone number and reassign to a different Sales Manager
        </Text>

        <Form
          form={form}
          layout="vertical"
          onFinish={handleSearch}
          style={{ marginTop: 24 }}
        >
          <Form.Item
            label="Phone Number"
            name="phoneNumber"
            rules={[
              { required: true, message: 'Please enter phone number' },
              { pattern: /^[0-9]{10}$/, message: 'Please enter a valid 10-digit phone number' }
            ]}
          >
            <Input
              placeholder="Enter 10-digit phone number"
              maxLength={10}
              suffix={
                <Button
                  type="primary"
                  icon={<SearchOutlined />}
                  htmlType="submit"
                  loading={searching}
                >
                  Search
                </Button>
              }
            />
          </Form.Item>
        </Form>

        {leadData && (
          <Card
            type="inner"
            title="Lead Details"
            style={{ marginTop: 24 }}
          >
            <Space direction="vertical" style={{ width: '100%' }} size="middle">
              <div>
                <Text strong>Name: </Text>
                <Text>{leadData.name || 'N/A'}</Text>
              </div>
              <div>
                <Text strong>Email: </Text>
                <Text>{leadData.email || 'N/A'}</Text>
              </div>
              <div>
                <Text strong>Phone: </Text>
                <Text>{leadData.phones?.[0]?.phone || 'N/A'}</Text>
              </div>
              <div>
                <Text strong>Lead ID: </Text>
                <Text>{leadData.lead_id || 'N/A'}</Text>
              </div>

              <Form
                form={form}
                layout="vertical"
                onFinish={handleReassign}
                style={{ marginTop: 16 }}
              >
                <Form.Item
                  label="Current Sales Manager"
                  name="currentManager"
                >
                  <Input disabled placeholder="Not assigned" />
                </Form.Item>

                <Form.Item
                  label="Current Manager Email"
                  name="currentManagerEmail"
                >
                  <Input disabled placeholder="N/A" />
                </Form.Item>

                <Form.Item
                  label="New Sales Manager"
                  name="newManagerEmail"
                  rules={[
                    { required: true, message: 'Please select a manager' }
                  ]}
                  extra="Select the manager you want to assign this lead to"
                >
                  {managersWithEmails.length > 0 ? (
                    <Select
                      showSearch
                      placeholder="Select manager"
                      optionFilterProp="children"
                      filterOption={(input, option) =>
                        (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                      }
                      options={managersWithEmails.map(manager => ({
                        label: `${manager.name} (${manager.label})`,
                        value: manager.email,
                        data: manager
                      }))}
                    />
                  ) : (
                    <Input
                      placeholder="manager@example.com"
                      type="email"
                    />
                  )}
                </Form.Item>

                <Form.Item>
                  <Button
                    type="primary"
                    htmlType="submit"
                    loading={loading}
                    icon={<SwapOutlined />}
                    block
                  >
                    Reassign Lead
                  </Button>
                </Form.Item>
              </Form>
            </Space>
          </Card>
        )}

        {!leadData && (
          <Alert
            message="No Lead Selected"
            description="Search for a lead using the phone number above to begin reassignment"
            type="info"
            showIcon
            style={{ marginTop: 24 }}
          />
        )}
      </Card>
    </div>
  );
}
